import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useAuthContext } from '../../../contexts/Auth-Context.js';
import DataTable, { createTheme } from 'react-data-table-component';
import { useSymbolContext } from '../../../contexts/Symbol-Context.js';
import { useChartContext } from '../../../contexts/Chart-Context.js';
import { FaTimes } from 'react-icons/fa';
import { InfoHexIcon, SettingsHexIcon } from './TableActionIcons.jsx';
import './Positions.css';
import { useAccountManagerContext } from '../../../contexts/Account-Manager-Context.js';
import { adjustDateTime, formatDate, formatDigitBasePrice, formatPrice, formatFixedTrimmed } from '../../../utils/format.js';
import { useMetricsContext } from "../../../contexts/Metrics-Context.js";

import { Ripple } from '../../utils/PopupRipple.jsx';
import 'react-ripple-click/dist/index.css';
import EditOrderSltp from '../edit_sltp_modal/Edit-Order-Sltp';
import Select from "../../utils/Select.jsx";
import useLongPress from "../../../lib/hooks/useLongPress.js";
import EditSltp from '../edit_sltp_modal/Edit-Sltp';
import CellText from './CellText.jsx';
import useTableHScrollPin from './useTableHScrollPin.js';
import { useTranslation } from 'react-i18next';
import { translateDirection } from '../../../i18n/directionLabel.js';
import NoRecordsMessage from '../../utils/NoRecordsMessage.jsx';
import { dropdownOptionStyle } from '../../../utils/dropdownOptionStyles.js';

const PRICE_DISPLAY_DECIMALS = 10;

function chunkArray(array, size) {
  let size2 = size;
  if (!Array.isArray(array)) {
    console.log('Please provide a valid array (in chunkArray function orders)');
    return [];
  }
  if (typeof size2 !== 'number' || size2 <= 0) {
    size2 = 5;
  }

  const chunkedArray = [];
  for (let i = 0; i < array.length; i += size2) {
    chunkedArray.push(array.slice(i, i + size2));
  }
  return chunkedArray;
}

const Orders = ({ openOrders,handleOpenOrdersUpdate, parentRef, currTab, handleRowClick, isSymbolSelecting }) => {
  const { t } = useTranslation();
  const { user ,platFromData, isFirstLoginDisabled} = useAuthContext();
  const { closeAllOrder, cancelOrder, closeBuyPendingOrders, closeSellPendingOrders } = useAccountManagerContext();
  const { symbolData } = useSymbolContext();
  const { selectedStyle, setSelectedStyle, styleOptions } = useChartContext();
  const [popupTitle, setPopupTitle] = useState();
  const { utcOffset} = useMetricsContext();
  const [closeAll, setCloseAll] = useState('false');
  const [selectedOrder, setSelectedOrder] = useState({ order_id: null, symbol: '', SL: null, TP: null, quantity: null });
  const [isEdit, setIsEdit] = useState(false);
  const [mobileMode, setMobileMode] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [isEditSltpVisible, setIsEditSltpVisible] = useState(false);
  const [popupOrder, setPopupOrder] = useState(null);
  const [showClosePopup, setShowClosePopup] = useState(false);
  const [showCloseAllPopup, setShowCloseAllPopup] = useState(false);
  const [selectedOrderRowId, setSelectedOrderRowId] = useState(null);
  const [isOrderClosing, setIsOrderClosing] = useState(false);
  const modalBottom = useRef(null);
  const tableScrollRef = useRef(null);
  useTableHScrollPin(tableScrollRef, !mobileMode);
  const positionDetailsView = (e) => {
    setSelectedOrder(e);
    setIsEdit(true);
  };

  const hasBuyPendingOrders = () => {
    return openOrders.some(order => 
      order.direction === 'Buy' && 
      order.current_price !== null && 
      order.current_price !== undefined
    );
  };

  const hasSellPendingOrders = () => {
    return openOrders.some(order => 
      order.direction === 'Sell' && 
      order.current_price !== null && 
      order.current_price !== undefined
    );
  };

  const popupAction = (e) => {
    if (popupTitle === 'close all orders') {
      clearAllOrders();
    } else if (popupTitle === 'Buy') {
      clearAllBuyOrders();
    } else if (popupTitle === 'Sell') {
      clearAllSellOrders();
    }
    setCloseAll("false");
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        modalBottom.current &&
        !modalBottom.current.contains(event.target)
      ) {
        setShowCloseAllPopup(false);
      }
    }

    if (showCloseAllPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCloseAllPopup]);

  useEffect(() => {
    const handleResize = () => {
      if(window.innerWidth <= 960) {
        setMobileMode(true);
      } else {
        setMobileMode(false);
      }
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    }
  }, []);

  const customStyles = {
    headCells: {
      style: {
        paddingLeft: '4px',
        paddingRight: '4px',
        justifyContent: 'flex-start',
        alignItems: 'center',
        textAlign: 'left',
      },
    },
    cells: {
      style: {
        paddingLeft: '4px',
        paddingRight: '4px',
        justifyContent: 'flex-start',
        alignItems: 'center',
        textAlign: 'left',
        cursor: !mobileMode ? 'pointer' : undefined,
      },
    },
    pagination: {
      style: {
        minHeight: '30px',
        height: '30px'
      },
    },
    headRow: {
      style: {
        minHeight: '10px',
        height: '30px',
        backgroundColor: 'var(--surface-panel, #252b42)',
      },
      denseStyle: {
        minHeight: '10px',
        height: '30px'
      },
    },
  };
  const orderRowConditionalStyles = [
    {
      when: (row) => row.order_id != null && row.order_id === selectedOrderRowId,
      classNames: ['am-row-selected'],
      style: {
        backgroundColor: '#323a54',
        color: 'var(--content-primary, #e8eaf0)',
        '&:hover': { backgroundColor: '#323a54' },
      },
    },
  ];
  createTheme(
    'solarized',
    {
      text: {
        primary: 'var(--content-secondary, #b8bcc8)', // normalTextColor
      },
      background: {
        default: 'var(--surface-panel, #252b42)', // firstBox
      },
      context: {
        background: 'var(--surface-panel, #252b42)', // firstBox
        text: 'var(--content-secondary, #b8bcc8)', // normalTextColor
      },
    },
    'dark',
  );

  const MobileOrderRow = ({ order, isExpanded, onToggle, onLongPressTrigger }) => {
    const handleLongPress = () => {
      onLongPressTrigger(order);
      console.log('Long press triggered');
    };

    const {
      handlers,
      isLongPress,
      cancel: cancelLongPress
    } = useLongPress(handleLongPress, 100);


    const handleClick = () => {
      if (isLongPress())
      return;
      onToggle();
      console.log('Single click');
    };
    return (
      <div className={`mobile-position-row ${isExpanded ? 'expanded' : ''}`}>
        <div onClick={handleClick} {...handlers}>
          <div className="main-row">
            <div className="left-section">
              <div className="symbol-direction">
                <span className="symbol-name">{order.symbol}</span>
                <span
                  style={{
                    color:
                      order.direction?.toLowerCase() === 'buy'
                        ? selectedStyle.buyColor
                        : selectedStyle.sellColor
                  }}
                >
                  {translateDirection(order.direction, t)}
                </span>
                <span className="quantity">{order.quantity ? formatFixedTrimmed(Number(order.quantity), 8) : '--'}</span>
              </div>
              <div className="price-movement">
                {order.entry_price ? formatFixedTrimmed(order.entry_price, PRICE_DISPLAY_DECIMALS) : '--'} → {order.current_price ? formatFixedTrimmed(order.current_price, PRICE_DISPLAY_DECIMALS) : '--'}
              </div>
            </div>
          </div>
          
          {isExpanded && (
            <div className="expanded-section">
              <div className="created-at">
                {formatDate(adjustDateTime(order.created_at, utcOffset))}
              </div>
            
              <div className="row">
                <div className="half-row">
                  <span className="label">S/L:</span>
                  <span className="value">{order.SL ? formatFixedTrimmed(order.SL, 8) : '--'}</span>
                </div>
                <div className="half-row">
                  <span className="label">T/P:</span>
                  <span className="value">{order.TP ? formatFixedTrimmed(order.TP, 8) : '--'}</span>
                </div>
              </div>
            
              <div className="row">
                <div className="half-row">
                  <span className="label">Margin:</span>
                  <span className="value">{order.margin ? formatFixedTrimmed(order.margin, 4) : '--'}</span>
                </div>
                <div className="half-row">
                  <span className="label">ID:</span>
                  <span className="value">{`PID${order.order_id.toString().padStart(8, "0")}`}</span>
                </div>
              </div>
            </div>          
          )}
        </div>
      </div>
    );
  };

  const MobileOrderList = ({ orders }) => {
    const toggleRow = (orderId) => {
      setExpandedRows(prev => ({
        ...prev,
        [orderId]: !prev[orderId]
      }));
    };    
    const handleLongPressTrigger = (order) => {
      setPopupOrder(order);
      setShowClosePopup(true);
    };
  
    return (
      <>
      <div className="mobile-positions-container">
        <div className="ellipsis-header">
          <button className="ellipsis-btn" onClick={() => setShowCloseAllPopup(true)}>
            ⋯
          </button>
        </div>
        {orders.map(order => (
          <MobileOrderRow
            key={order.order_id}
            order={order}
            isExpanded={expandedRows[order.order_id]}
            onToggle={() => toggleRow(order.order_id)}
            onLongPressTrigger={handleLongPressTrigger}
          />
        ))}
      </div>
      {showCloseAllPopup && (
        <div className="bottom-popup-overlay" onClick={() => setShowCloseAllPopup(false)} ref={modalBottom}>
          <div className="bottom-popup" onClick={(e) => e.stopPropagation()}>
            <h3 className="popup-heading">{t('actions.bulkOperations')}</h3>

            <div className="bulk-buttons-container">
              <button
                className="bulk-button"
                disabled={isFirstLoginDisabled || (platFromData[5]?.riskLocked || platFromData[5]?.accessRight == 3 || localStorage.getItem('accountType') == "0" || openOrders?.length === 0 )}
                onClick={() => {
                  setCloseAll('true');
                  setPopupTitle('close all orders');
                }}
              >
                {t('actions.closeAllOrders')}
              </button>

              {hasBuyPendingOrders() && (
                <button
                  className="bulk-button"
                  onClick={() => {
                    setPopupTitle('Buy');
                    setShowCloseAllPopup(false);
                    setCloseAll('true');
                  }}
                >
                  {t('actions.closeBuyPendingOrders')}
                </button>
              )}

              {hasSellPendingOrders() && (
                <button
                  className="bulk-button"
                  onClick={() => {
                    setPopupTitle('Sell');
                    setShowCloseAllPopup(false);
                    setCloseAll('true');
                  }}
                >
                  {t('actions.closeSellPendingOrders')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showClosePopup && popupOrder && (
          <div className="bottom-popup-overlay2" onClick={() => setShowClosePopup(false)}>
            <div className="bottom-popup2" onClick={(e) => e.stopPropagation()}>
              <button
                className="close-all-button"
                onClick={() => {
                  if (popupOrder && popupOrder.current_price) {
                    cancelOrder(user.userId, popupOrder, popupOrder.current_price, 'cancelled');
                    setShowClosePopup(false);
                  } else {
                    console.error("Cannot cancel order: missing current price");
                  }
                }}
              >
                Close Order
              </button>
            </div>
          </div>
        )}
      </>
    );
  };
  
  const columns = useMemo(() => [
    {
      name: "",
      cell: (row) => (
        <div className="positions-button">
          <button role="info" className="modal-icon" 
          onClick={() => positionDetailsView(row)}
          >
          <InfoHexIcon />
          </button>
          <button
            className="modal-icon modal-icon-sltp"
            role="info"
            onClick={() => {
              showEditSltp();
              setSelectedOrder(row)
            }}
          >
          <SettingsHexIcon />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      nonSortable: true,
      grow: 0,
      width: '90px',
      minWidth: '90px',
      maxWidth: '90px',
      wrap:false
    },
    {
      name: t('tables.id'),
      id: 'id',
      selector: (row) => row.order_id,
      grow: 0,
      minWidth: '108px',
      wrap:false,
      cell: (row) => <span className="cell-id">{row.order_id}</span>,
    },

    {
      name: t('tables.created'),
      selector: (row) =>
        formatDate(adjustDateTime(row.created_at, utcOffset)),
      minWidth: '124px',
      grow: 1,
      wrap: false,
      cell: (row) => (
        <CellText text={formatDate(adjustDateTime(row.created_at, utcOffset))} />
      ),
    },
    {
      name: t('tables.symbol'),
      selector: (row) => row.symbol,
      grow: 0,
      minWidth: '72px',
      wrap:false,
      cell: (row) => <CellText text={row.symbol} />,
    },
    {
      name: t('tables.quantity'),
      selector: (row) => formatFixedTrimmed(row.quantity, 8),
      minWidth: '56px',
      grow: 0,
      wrap: false,
      cell: (row) => <CellText text={formatFixedTrimmed(row.quantity, 8)} />,
    },
    {
      name: t('tables.direction'),
      selector: (row) => row.direction,
      grow: 0,
      minWidth: '48px',
      wrap:false,
      cell: (row) => <CellText text={translateDirection(row.direction, t)} />,
    },
    {
      name: t('tables.margin'),
      selector: (row) => formatFixedTrimmed(row.margin, 4),
      grow: 0,
      minWidth: '52px',
      wrap:false,
      cell: (row) => <CellText text={formatFixedTrimmed(row.margin, 4)} />,
    },
    {
      name: t('tables.entry'),
      selector: (row) => formatFixedTrimmed(row.entry_price, PRICE_DISPLAY_DECIMALS),
      grow: 0,
      minWidth: '52px',
      wrap:false,
      cell: (row) => <CellText text={formatFixedTrimmed(row.entry_price, PRICE_DISPLAY_DECIMALS)} />,
    },
    {
      name: t('tables.current'),
      selector: (row) => row.current_price ? formatFixedTrimmed(row.current_price, PRICE_DISPLAY_DECIMALS) : '--',
      grow: 0,
      minWidth: '52px',
      wrap:false,
      cell: (row) => (
        <CellText
          text={row.current_price ? formatFixedTrimmed(row.current_price, PRICE_DISPLAY_DECIMALS) : '--'}
        />
      ),
    },
    {
      name: t('tables.sl'),
      selector: (row) =>
        String(row.SL) !== '' ? formatFixedTrimmed(row.SL, 8) : '--',
      grow: 0,
      minWidth: '48px',
      wrap:false,
      cell: (row) => (
        <CellText text={String(row.SL) !== '' ? formatFixedTrimmed(row.SL, 8) : '--'} />
      ),
    },
    {
      name: t('tables.tp'),
      selector: (row) =>
        String(row.TP) !== '' ? formatFixedTrimmed(row.TP, 8) : '--',
      grow: 0,
      minWidth: '48px',
      wrap:false,
      cell: (row) => (
        <CellText text={String(row.TP) !== '' ? formatFixedTrimmed(row.TP, 8) : '--'} />
      ),
    },
    {
      id: 'close',
      name: (
        <button
          className={`Close-button`}
          disabled={isFirstLoginDisabled || (platFromData[5]?.riskLocked || platFromData[5]?.accessRight == 3 || localStorage.getItem('accountType') == "0" || openOrders?.length === 0 )}
          onClick={() => {
            setCloseAll('true');
            setPopupTitle('close all orders');
          }}
        >
          {isOrderClosing ? t('actions.cancelling') : t('actions.cancelAll')}
        </button>
      ),
      cell: (row) => (
        <div className="positions-button">
          <button
            className="react-icon"
            disabled={isFirstLoginDisabled || platFromData[5]?.riskLocked || platFromData[5]?.accessRight == 3 || localStorage.getItem('accountType') =="0" ||(row.current_price ? false : true)}
            onClick={() => {
              cancelOrder(user.userId, row, row.current_price, 'cancelled');
            }}
          >
            <FaTimes />
          </button>
        </div>
      ),
      grow: 0,
      width: '76px',
      minWidth: '76px',
      maxWidth: '76px',
      wrap:false,
      center: true,
      style: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        paddingLeft: '0',
        paddingRight: '0',
        textAlign: 'center',
      },
    },
  ], [t, isOrderClosing, openOrders, isFirstLoginDisabled, platFromData, user, utcOffset]);
  const showEditSltp = () => {
    setIsEditSltpVisible(true);
  };

  const hideEditSltp = () => {
    setIsEditSltpVisible(false);
  };

  // Handle order row double click to open SL/TP modal
  const handleOrderRowDoubleClick = (row) => {
    if (!row) return;
    setSelectedOrder(row);
    showEditSltp();
  };
  const paginationComponentOptions = {
    noRowsPerPage: true,
  };
  const clearAllOrders = async () => {
    const currentDate = new Date();
    closeAllOrder(user.userId);
  };
  
  const clearAllBuyOrders = async () => {
    closeBuyPendingOrders(user.userId, 'Buy');
  }

  const clearAllSellOrders = async () => {
    closeSellPendingOrders(user.userId, 'Sell');
  }

  const customStyle = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '24px',
      height: '24px',
      width: '124px',
      minWidth: '124px',
      maxWidth: '124px',
      margin: 0,
      borderColor: state.isFocused
        ? `${selectedStyle.buyColor} !important`
        : 'var(--surface-border, #3a4156) !important',
      backgroundColor: 'var(--surface-panel, #252b42) !important',
      borderRadius: '6px',
      boxShadow: 'none',
      cursor: 'pointer',
      alignItems: 'center',
      boxSizing: 'border-box',
      '&:hover': {
        borderColor: `${selectedStyle.buyColor} !important`,
        boxShadow: 'none',
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      paddingLeft: '0',
      margin: '0',
      color: 'var(--content-secondary, #b8bcc8) !important',
      fontSize: '11px',
      lineHeight: '1',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 2px 0 6px',
      height: '26px',
      display: 'flex',
      alignItems: 'center',
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: '26px',
      alignSelf: 'center',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      padding: '0 4px',
      color: state.isFocused || state.selectProps.menuIsOpen
        ? `${selectedStyle.buyColor} !important`
        : 'var(--content-secondary, #b8bcc8) !important',
      transition: 'color 0.15s ease',
      transform: 'none',
      '&:hover': {
        color: `${selectedStyle.buyColor} !important`,
      },
      svg: {
        width: '14px',
        height: '14px',
        transition: 'transform 0.2s ease',
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transformOrigin: 'center',
      },
    }),
    option: (provided, state) => dropdownOptionStyle(provided, state, {
      padding: '0 12px',
      height: '24px',
      minHeight: '24px',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      fontSize: '11px',
      lineHeight: '1.35',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'var(--surface-panel, #252b42) !important',
      margin: '0.3rem 0 0 0 !important',
      zIndex: 9999,
      overflow: 'hidden',
      borderRadius: '6px',
      border: '1px solid var(--surface-border, #3a4156)',
      boxShadow: 'none',
      minWidth: '124px',
      width: '124px',
      maxWidth: '124px',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '132px',
      minHeight: '3px',
      width: '100%',
      overflowY: 'auto',
      padding: '2px 0',
      scrollbarWidth: 'thin',
      scrollbarColor: 'rgba(170, 170, 170, 0.45) transparent',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };
  
  const [filterText, setFilterText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const directionOptions = useMemo(
    () => [
      { value: 'all', label: t('filters.allDirections') },
      { value: 'Buy', label: t('common.buy') },
      { value: 'Sell', label: t('common.sell') },
    ],
    [t],
  );
  const [directionValue, setDirectionValue] = useState('all');
  const direction = directionOptions.find((o) => o.value === directionValue) ?? directionOptions[0];

  useEffect(() => {
    filterData(filterText, directionValue);
  }, [openOrders, filterText, directionValue]);

  const handleFilter = (e) => {
    const value = e.target.value;
    setFilterText(value);
  };

  const handleSelectChange = (selectedOption) => {
    setDirectionValue(selectedOption?.value ?? 'all');
  };

  const filterData = (text, direction) => {
    // Sort by created_at descending (newest first) first
    let filteredItems = [...openOrders].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateB - dateA;
    });

    if (direction !== 'all') {
      filteredItems = filteredItems.filter((item) => item.direction === direction);
    }

    if (text) {
      filteredItems = filteredItems.filter(
        (item) =>
          item.symbol.toLowerCase().includes(text.toLowerCase()) ||
          item.order_id.toString().toLowerCase().includes(text.toLowerCase())
      );
    }
    handleOpenOrdersUpdate(filteredItems.length)
    setFilteredData(filteredItems);
  };

  // useEffect that calculates pagination rows based on height of container
  const [currentPage, setCurrentPage] = useState(1)
  const [paginationPerPage, setPaginationPerPage] = useState(10);
  const [filteredDataCopy, setFilteredDataCopy] = useState([]);
  const ordersPerPage = 9;
  const indexOfLast = currentPage * ordersPerPage;
  const indexOfFirst = indexOfLast - ordersPerPage;
  const currentorders = filteredData.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredData.length / ordersPerPage);

  const handlePageChange = (pg) => {
    setCurrentPage(pg);
  };

  useEffect(() => {
    if (window.innerWidth < 990) return;
    const calculateRowsPerPage = () => {
      setPaginationPerPage(10);
      if (parentRef?.current) {
        const divHeight = parentRef.current.clientHeight;
        const rowHeight = 35;
        let rows = 10;
        // rows = rows > 10 ? 10 : rows;
        // setPaginationPerPage(rows);
        setFilteredDataCopy(chunkArray(filteredData, rows));
      } else {
        // setPaginationPerPage(5);
        setFilteredDataCopy(chunkArray(filteredData, 10));
      }
    };
    // Call the function if currTab = history
    if (currTab === 'history') calculateRowsPerPage();
  }, [currTab, filteredData, parentRef]);

  return (
    <>
            {isEdit && (
        <>
          <div className="position-info-backdrop"></div>
          <div className="wm-parent" onClick={() => setIsEdit(false)}>
            <div
              className="window-module"
              style={{ position: "fixed", top: "25%", zIndex: 999 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="wm-header">
                <h2 className="wmTitleDynamic">
                  Order Info: {selectedOrder.order_id}
                </h2>
                <div
                  className="wm-exit close-icon"
                  onClick={() => {
                    setIsEdit(false);
                  }}
                >
                  <FaTimes />
                </div>
              </div>
              <div className="wm-content">
                {/* First row: Order ID, Created Date Time, Symbol Name, Direction (4 fields) */}
                <div className="input-content-wm">
                  <div className="row row-4">
                    <div className="wm-read-p-in">
                      <p>{t('tables.orderId')}</p>
                      <input
                        type="text"
                        readOnly
                        value={selectedOrder.order_id}
                      />
                    </div>
                    <div className="wm-read-p-in">
                      <p>{t('tables.createdDateTime')}</p>
                      <input
                        type="text"
                        readOnly
                        value={`${new Date(
                          selectedOrder.created_at
                        ).toLocaleDateString("en-GB")}, ${new Date(
                          selectedOrder.created_at
                        ).toLocaleTimeString("en-GB", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })}`}
                      />
                    </div>
                    <div className="wm-read-p-in">
                      <p>{t('tables.symbolName')}</p>
                      <input
                        type="text"
                        readOnly
                        value={selectedOrder.symbol}
                      />
                    </div>
                    <div className="wm-read-p-in">
                      <p>{t('tables.direction')}</p>
                      <input
                        type="text"
                        readOnly
                        value={translateDirection(selectedOrder.direction, t)}
                      />
                    </div>
                  {/* </div>
                </div> */}
                {/* Second row: Entry, Margin, Quantity, Current (4 fields) */}
                {/* <div className="input-content-wm">
                  <div className="row row-4"> */}
                    <div className="wm-read-p-in">
                      <p>{t('tables.entry')}</p>
                      <input
                        type="text"
                        readOnly
                        value={formatFixedTrimmed(selectedOrder.entry_price, PRICE_DISPLAY_DECIMALS)}
                      />
                    </div>
                    <div className="wm-read-p-in">
                      <p>{t('tables.margin')}</p>
                      <input
                        type="text"
                        readOnly
                        value={formatFixedTrimmed(selectedOrder.margin, 4)}
                      />
                    </div>
                    <div className="wm-read-p-in">
                      <p>{t('tables.quantity')}</p>
                      <input
                        type="text"
                        readOnly
                        value={formatFixedTrimmed(selectedOrder.quantity, 8)}
                      />
                    </div>
                    <div className="wm-read-p-in">
                      <p>{t('tables.currentPrice')}</p>
                      <input
                        type="text"
                        readOnly
                        value={selectedOrder?.current_price ? formatFixedTrimmed(selectedOrder.current_price, PRICE_DISPLAY_DECIMALS) : "---"}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {closeAll === "true" && (
        <div className="confirm-box" role={closeAll}>
          <div className="card">
            <h2>
              {popupTitle === 'close all orders' && t('actions.confirmCloseAllOrders')}
              {popupTitle === 'Buy' && t('actions.confirmCloseBuyOrders')}
              {popupTitle === 'Sell' && t('actions.confirmCloseSellOrders')}
            </h2>
            <div className="row-scss">
              <button
                onClick={(e) => {
                  setCloseAll('false');
                }}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  isolation: 'isolate',
                  backgroundColor: selectedStyle.sellColor,
                }}
              >
                <Ripple/>
                {t('common.cancel')}
              </button>
              <div className="blank"></div>
              <button
                onClick={(e) => {
                  popupAction(e);
                }}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  isolation: 'isolate',
                  backgroundColor: selectedStyle.buyColor,
                }}
              >
                <Ripple />
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
      {isEditSltpVisible && 
      <EditSltp  
      onCancel={hideEditSltp} 
      editPositionId={selectedOrder.order_id}  
      currentPrice={openOrders.find(  (pos) => 
        pos.symbol === selectedOrder.symbol && pos.order_id === selectedOrder.order_id  )
      } 
      isOrder={true} />}
      <div
        className="open-positions-table bottom-table-bstorm"
        style={{ overflow: 'hidden' }}
      >
        <div className="positions-button history-filters">
          <div className="hf-group hf-search">
            <div className="input-text">
              <input
                type="text"
                value={filterText}
                onChange={handleFilter}
                placeholder={t('tables.searchSymbol')}
              />
              <div className="input-svg" id="searchSvgColor">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="11"
                  fill="currentColor"
                  className="bi bi-search"
                  viewBox="0 0 16 16"
                >
                  <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" />
                </svg>
              </div>
            </div>
          </div>
          <div className="hf-group hf-direction">
            <span className="hf-label">{t('filters.direction')}</span>
            <Select
              styles={customStyle}
              value={direction}
              onChange={handleSelectChange}
              options={directionOptions}
              isSearchable={false}
              menuPortalTarget={document.body}
              classNamePrefix="history-direction"
            />
          </div>
        </div>
        { mobileMode ? (
          <>
            <MobileOrderList orders={currentorders} />
            {filteredData.length > ordersPerPage && (
              <div className="pagination-controls">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" fill="currentColor"
                      viewBox="0 0 16 16">
                    <path d="M11.354 1.146a.5.5 0 0 1 0 .708L6.207 7l5.147 5.146a.5.5 0 0 1-.708.708l-5.5-5.5a.5.5 0 0 1 0-.708l5.5-5.5a.5.5 0 0 1 .708 0z"/>
                    <path d="M7.354 1.146a.5.5 0 0 1 0 .708L2.207 7l5.147 5.146a.5.5 0 0 1-.708.708l-5.5-5.5a.5.5 0 0 1 0-.708l5.5-5.5a.5.5 0 0 1 .708 0z"/>
                  </svg>
                </button>
              
                <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" fill="currentColor"
                      viewBox="0 0 16 16">
                    <path d="M11.354 1.146a.5.5 0 0 1 0 .708L6.207 7l5.147 5.146a.5.5 0 0 1-.708.708l-5.5-5.5a.5.5 0 0 1 0-.708l5.5-5.5a.5.5 0 0 1 .708 0z"/>
                  </svg>
                </button>
              
                <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" fill="currentColor"
                      viewBox="0 0 16 16">
                    <path d="M4.646 1.146a.5.5 0 0 1 .708 0l5.5 5.5a.5.5 0 0 1 0 .708l-5.5 5.5a.5.5 0 1 1-.708-.708L9.793 7 4.646 1.854a.5.5 0 0 1 0-.708z"/>
                  </svg>
                </button>
              
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" fill="currentColor"
                      viewBox="0 0 16 16">
                    <path d="M4.646 1.146a.5.5 0 0 1 .708 0L10.5 6.293 5.354 11.44a.5.5 0 0 1-.708-.708L9.293 7 4.646 2.354a.5.5 0 0 1 0-.708z"/>
                    <path d="M8.646 1.146a.5.5 0 0 1 .708 0L14.5 6.293 9.354 11.44a.5.5 0 1 1-.708-.708L13.293 7 8.646 2.354a.5.5 0 0 1 0-.708z"/>
                  </svg>
                </button>
              </div>
              )}
          </>
        ) : (
        <div className="table-scroll-area" ref={tableScrollRef}>
        <DataTable
          className="history-rdt"
          columns={columns}
          data={filteredDataCopy[currentPage - 1] || filteredData}
          persistTableHead
          responsive={false}
          fixedHeader={false}
          pagination={false}
          paginationTotalRows={filteredData.length}
          onChangePage={handlePageChange}
          paginationDefaultPage={currentPage}
          paginationServer
          paginationPerPage={paginationPerPage}
          theme="solarized"
          customStyles={customStyles}
          conditionalRowStyles={orderRowConditionalStyles}
          paginationComponentOptions={paginationComponentOptions}
          noDataComponent={<NoRecordsMessage />}
          pointerOnHover
          onRowClicked={(row) => {
            if (!row) return;
            setSelectedOrderRowId(row.order_id);
            handleRowClick?.(row);
          }}
          onRowDoubleClicked={handleOrderRowDoubleClick}
        />
        {!mobileMode && (filteredDataCopy[currentPage - 1] || filteredData)?.length > 0 && (
          <div className="no-more-inline">{t('tables.noMoreOrders')}</div>
        )}
        </div>
      )}
      </div>
    </>
  );
};

export default Orders;
